import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

export function AutocompleteTagInput({
  suggestions = [],
  value = [],
  onChange,
  placeholder = 'Type to search...',
  label,
  className = '',
  inputClassName = '',
  tagsClassName = '',
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filtered = query.trim()
    ? suggestions
        .filter(
          (s) =>
            s.toLowerCase().includes(query.toLowerCase()) &&
            !value.some((v) => v.toLowerCase() === s.toLowerCase())
        )
        .slice(0, 8)
    : [];

  useEffect(() => {
    setHighlightIndex(-1);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addItem = (item) => {
    const trimmed = item.trim();
    if (!trimmed) return;
    if (value.some((v) => v.toLowerCase() === trimmed.toLowerCase())) return;
    onChange([...value, trimmed]);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen && filtered.length > 0) {
        setIsOpen(true);
        setHighlightIndex(0);
      } else if (isOpen) {
        setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightIndex >= 0 && highlightIndex < filtered.length) {
        addItem(filtered[highlightIndex]);
      } else if (query.trim()) {
        addItem(query);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setIsOpen(val.trim().length > 0 && filtered.length > 0);
  };

  // Re-check if dropdown should be open when filtered changes
  useEffect(() => {
    if (query.trim() && filtered.length > 0) {
      setIsOpen(true);
    } else if (filtered.length === 0) {
      setIsOpen(false);
    }
  }, [filtered.length, query]);

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="admin-filter-label">{label}</label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.trim() && filtered.length > 0) setIsOpen(true);
          }}
          className={`admin-filter-control ${inputClassName}`}
          placeholder={placeholder}
        />
        {isOpen && filtered.length > 0 && (
          <ul
            ref={dropdownRef}
            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
          >
            {filtered.map((item, idx) => (
              <li
                key={item}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addItem(item);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`px-2.5 py-1.5 text-sm cursor-pointer transition-colors ${
                  idx === highlightIndex
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
      {value.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${tagsClassName}`}>
          {value.map((item, index) => (
            <span
              key={index}
              className="admin-filter-chip"
            >
              {item}
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-primary-400 hover:text-primary-700 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
